/**
 * @fileoverview WEB-711 — `SeatTierModule`, the sports spine's seat-tier list
 * ("Where to sit").
 *
 * ## What this renders
 *
 * The plain shared row list (`ModuleRowList`) — title, a meta describing the
 * seating position, and a trailing price — per
 * `docs/design-system/event-v2-m2-module-specs.md` §2.5. No lead, no visual.
 *
 * ## Why it ships fully degraded today
 *
 * `EventDetailV2.meta` carries `priceTierCount` (a COUNT) and `seatmapUrl`,
 * but no per-tier LIST — no tier name, no seating position, no per-tier
 * price. `resolveSeatTierEntries` is the seam a future supply project fills
 * in; until then it always returns `[]`, so `shouldListTiers` is always false
 * and this module falls through to the `seatmapUrl` fallback (a real value we
 * do hold) or renders nothing. That is the design spec's degrade-case index
 * (§11): "no tier list and no per-tier price column ... offer `seatmapUrl` if
 * present" — a LOCI addition the mockup itself does not show (§2.5).
 *
 * `module.hasContent` is the OUTER gate — `hasModuleContent` in
 * `lib/eventDetail/modules.ts` resolves `seatTiers` to `true` when
 * `event.meta.seatmapUrl` is a usable http(s) URL, `ticketLimitInfo` is
 * a non-empty string (WEB-806), or the server assembled a price story
 * (WEB-851). This file adds no second gating opinion on top of it.
 * Everything below decides WHAT to render once that outer gate has already
 * said yes.
 *
 * ## WEB-851 — this is the ticketing section, not a seat-tier list
 *
 * Because the tier list has never existed, what this module actually renders
 * is whatever ticketing facts the payload carries. It now leads with the
 * price story `decisionPanel.cta` already publishes under the CTA, with the
 * provider attribution linked to the CTA's own destination. The music
 * lexicon heading moved to "Ticketing Information" to match, its degrade
 * copy no longer singles out "seat tiers or prices", and the kind opts out
 * of the "NOT LISTED" section meta — an Eventbrite row with a published band
 * and no seatmap used to render all three of those at once, denying a price
 * the same page was printing two inches above.
 */

import type { ReactElement } from 'react';

import { TextIcon } from '@/components/atoms/TextIcon';
import { cn } from '@/components/atoms/primitives/utils';
import {
  EventModuleSection,
  ModuleRowList,
  type ModuleRow,
} from '@/components/organisms/EventModuleSection';
import {
  resolveTicketingPriceNote,
  type TicketingPriceNote,
} from '@/lib/eventDetail/ticketingPriceNote';
import { isUsableHttpUrl } from '@/lib/ticketing';
import type { EventDetailV2 } from '@/types/eventDetail';

import type { EventModuleProps } from './registry';

/** One seat tier's row content: a name, its seating position, and its price. */
export interface SeatTierEntry {
  id: string;
  title: string;
  position: string;
  price: string;
}

const SEAT_MAP_LINK_LABEL = 'View seat map';

/** 44px — the WCAG 2.2 AA minimum touch target, matching `EventModuleSection`'s "more" link. */
const SEAT_MAP_LINK_MIN_SIZE_CLASS = 'min-h-11 min-w-11';

/**
 * Awaiting supply: `EventDetailV2` has a price-tier COUNT
 * (`event.meta.priceTierCount`) but no tier LIST — no per-tier name,
 * position, or price. Always `[]` today; this is the seam a future contract
 * change fills in. Only this function's body should change then.
 */
export function resolveSeatTierEntries(_event: EventDetailV2): readonly SeatTierEntry[] {
  return [];
}

export function seatTierEntriesToRows(entries: readonly SeatTierEntry[]): ModuleRow[] {
  return entries.map((entry) => ({
    id: entry.id,
    title: entry.title,
    meta: entry.position,
    trail: entry.price,
  }));
}

/**
 * "priceTierCount <= 1 or no tier list" degrades (WEB-711 AC). A single known
 * tier is not worth a list; an unknown count does not veto a real list.
 */
export function shouldListTiers(
  entries: readonly SeatTierEntry[],
  tierCount: number | null,
): boolean {
  return entries.length > 1 && (tierCount === null || tierCount > 1);
}

/**
 * Trims, then **rejects any scheme that is not http(s)**.
 *
 * Without the scheme check, a payload carrying `javascript:…` or `data:…`
 * would be written straight into the `<a href>` below and execute on click.
 * The field has no backing column today, so this is latent rather than live —
 * which is exactly why it has to be closed now: the day `seatmapUrl` starts
 * arriving is the day an unguarded sink becomes reachable, and nobody will
 * re-audit this function then.
 *
 * Reuses `isUsableHttpUrl` (`lib/ticketing.ts`) rather than adding a second
 * opinion on what a safe URL is — it is the same allow-http(s)-only check the
 * ticket CTA already applies to externally-supplied links.
 */
function normalizeSeatmapUrl(seatmapUrl: string | null | undefined): string | null {
  if (typeof seatmapUrl !== 'string') {
    return null;
  }
  const trimmed = seatmapUrl.trim();
  return isUsableHttpUrl(trimmed) ? trimmed : null;
}

interface SeatMapLinkProps {
  href: string;
}

function trimTicketLimitInfo(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The price line's register: the same mono / caps / 0.06em data voice the
 * decision panel prints this exact string in, one step up the size ladder
 * (13px, the module row `lead` size) and in `--fg-1` rather than `--fg-3`.
 * Under the CTA the note is a footnote to a button; here it is the section's
 * headline fact, and 10px `--fg-3` inside an editorial column reads as
 * disclaimer text.
 */
const PRICE_NOTE_CLASS = [
  'text-small font-normal uppercase tracking-[0.06em]',
  'font-mono text-fg-1',
].join(' ');

interface TicketingPriceLineProps {
  note: TicketingPriceNote;
}

/**
 * WEB-851 — the price story, with the provider attribution linked to the same
 * destination the CTA button uses.
 *
 * The anchor wraps the provider name only, per the issue's spec
 * (`$20.38–$23.69 · VIA <link>EVENTBRITE</link>`), and carries an
 * `aria-label` because "EVENTBRITE" alone is a link name that says nothing
 * about where it goes. The label contains the visible text verbatim, so
 * WCAG 2.5.3 (Label in Name) still holds.
 *
 * No 44px touch floor here, unlike {@link SeatMapLink}: this is a link inside
 * a sentence, which is exactly the inline exception WCAG 2.5.8 carves out,
 * and padding it to 44px would break the line it sits in.
 */
function TicketingPriceLine({ note }: TicketingPriceLineProps): ReactElement {
  return (
    <p className={cn('m-0 wrap-break-word', PRICE_NOTE_CLASS)} data-testid="seat-tier-price-note">
      {note.lead}
      {note.sourceLabel !== null ? (
        note.href !== null ? (
          <a
            href={note.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View this event on ${note.sourceName ?? note.sourceLabel}`}
            data-testid="seat-tier-price-source-link"
            // The underline is NOT decorative — do not drop it. This is a
            // link inside a text block, so WCAG 1.4.1 needs a non-colour
            // distinguisher unless the link/copy contrast clears 3:1, and
            // `--accent-text` against `--fg-1` is 2.09:1 dark / 1.98:1 light.
            // axe's `link-in-text-block` caught exactly that on the music spine
            // stories (WEB-851).
            //
            // WEB-851 had to write both declarations as an inline `style`; the
            // utilities work now (WEB-853 layered the `a` reset that was eating
            // them — see apps/web/CLAUDE.md). Asserting the CLASS is honest
            // coverage only because that cascade is pinned in the same required
            // gate by `styles/__tests__/anchor-reset-layer.guard.test.ts`.
            className={cn(
              'text-accent-text underline underline-offset-2',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg-1',
            )}
          >
            {note.sourceLabel}
          </a>
        ) : (
          // No destination — the attribution is still true, so it prints as
          // text rather than becoming a dead link.
          note.sourceLabel
        )
      ) : null}
    </p>
  );
}

interface TicketLimitNoteProps {
  text: string;
}

function TicketLimitNote({ text }: TicketLimitNoteProps): ReactElement {
  return (
    <p
      className="m-0 max-w-[60ch] wrap-break-word whitespace-pre-wrap leading-relaxed text-fg-2"
      data-testid="seat-tier-ticket-limit"
    >
      {text}
    </p>
  );
}

/**
 * The seat-map fallback — a LOCI addition the mockup does not show (§2.5), so
 * there is no measured spec for its own visual treatment. Composes the
 * existing, spec'd "more" link pattern (§0.4: Space Grotesk / 13px / 600 /
 * `--teal` / 44px touch target / trailing arrow) rather than inventing a new
 * one. The ink is `--accent-text` (WEB-650 accent-as-text), not the
 * `--accent` fill/stroke role — same pairing `RunOfDatesStrip` uses.
 */
function SeatMapLink({ href }: SeatMapLinkProps): ReactElement {
  return (
    <a
      href={href}
      data-testid="seat-tier-seatmap-link"
      // WEB-851 set `fontSize` + `color` inline here and recorded that neither
      // `text-[var(--accent-text)]` nor `[color:var(--accent-text)]` "compiled
      // a color rule". That diagnosis was wrong: WEB-853 compiled all three
      // candidate forms through this repo's own Tailwind and every one emitted
      // `color: var(--accent-text)`. The rule was compiled correctly and then
      // DEFEATED — by the unlayered `a { color: inherit }` reset in
      // `globals.css`, which beat every layered declaration at any specificity.
      // That reset now lives in `@layer base`, so the utilities win.
      //
      // Both arbitrary values carry an explicit data-type hint. `cn()`'s
      // tailwind-merge buckets the ambiguous `text-[…]` forms together, so a
      // bare size and a bare colour on one element silently drop one another.
      className={cn(
        'inline-flex items-center gap-2 font-semibold',
        'font-display text-small text-accent-text',
        SEAT_MAP_LINK_MIN_SIZE_CLASS,
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg-1',
      )}
    >
      {SEAT_MAP_LINK_LABEL}
      <TextIcon />
    </a>
  );
}

/**
 * `SeatTierModule` — registered `seatTiers` renderer (WEB-711).
 */
export function SeatTierModule({ module, event, chroma }: EventModuleProps): ReactElement | null {
  if (!module.hasContent) {
    return null;
  }

  const entries = resolveSeatTierEntries(event);
  const priceNote = resolveTicketingPriceNote(event);
  const ticketLimitInfo = trimTicketLimitInfo(event.meta.ticketLimitInfo);
  const seatmapUrl = normalizeSeatmapUrl(event.meta.seatmapUrl);

  // Price first in both branches: it is the one thing a reader opens a
  // ticketing section to find, and it is the fact WEB-851 was filed because
  // the page was denying it held.
  if (shouldListTiers(entries, event.meta.priceTierCount)) {
    return (
      <EventModuleSection module={module} chroma={chroma}>
        <div className="flex min-w-0 flex-col gap-3">
          {priceNote !== null ? <TicketingPriceLine note={priceNote} /> : null}
          {ticketLimitInfo ? <TicketLimitNote text={ticketLimitInfo} /> : null}
          <ModuleRowList rows={seatTierEntriesToRows(entries)} chroma={chroma} />
        </div>
      </EventModuleSection>
    );
  }

  if (priceNote !== null || seatmapUrl !== null || ticketLimitInfo) {
    return (
      <EventModuleSection module={module} chroma={chroma}>
        <div className="flex min-w-0 flex-col gap-3">
          {priceNote !== null ? <TicketingPriceLine note={priceNote} /> : null}
          {ticketLimitInfo ? <TicketLimitNote text={ticketLimitInfo} /> : null}
          {seatmapUrl !== null ? <SeatMapLink href={seatmapUrl} /> : null}
        </div>
      </EventModuleSection>
    );
  }

  return null;
}
